/**
 * assessmentSchedule
 *
 * Client-side helpers for computing "last assessed" / "next due" display
 * values for the recurring assessment schedule.
 *
 * Mirrors the period logic in RecurringAssessmentService on the backend.
 */

import { AssessmentCadence } from '../graphql/types';
import type { MaturityAssessment } from '../graphql/types';

// ─── Period helpers ────────────────────────────────────────────────────────

/**
 * Return the ISO 8601 quarter string for a given date.
 * @example periodForDate(new Date('2026-07-01')) === '2026-Q3'
 */
export function periodForDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 1-based
  let quarter: 1 | 2 | 3 | 4;
  if (month <= 3) quarter = 1;
  else if (month <= 6) quarter = 2;
  else if (month <= 9) quarter = 3;
  else quarter = 4;
  return `${year}-Q${quarter}`;
}

/**
 * Return the first day of the quarter period as a UTC Date.
 * @example periodStart('2026-Q3') === 2026-07-01T00:00:00Z
 */
export function periodStart(period: string): Date {
  const [yearStr, qStr] = period.split('-Q');
  const year = parseInt(yearStr, 10);
  const quarter = parseInt(qStr, 10);
  const month = (quarter - 1) * 3; // 0-based month index
  return new Date(Date.UTC(year, month, 1));
}

/**
 * Advance to the next period that falls within the given cadence.
 *
 * Starts from the quarter *after* `fromPeriod` and returns the next
 * period the scheduler would fire for the specified cadence.
 */
export function nextScheduledPeriod(
  fromPeriod: string,
  cadence: AssessmentCadence = AssessmentCadence.SemiAnnual
): string {
  const [yearStr, qStr] = fromPeriod.split('-Q');
  let year = parseInt(yearStr, 10);
  let quarter = parseInt(qStr, 10);

  // Advance at least one quarter
  quarter += 1;
  if (quarter > 4) {
    quarter = 1;
    year += 1;
  }

  // Keep advancing until the period matches the cadence
  for (let i = 0; i < 4; i++) {
    const candidate = `${year}-Q${quarter}`;
    if (periodMatchesCadence(candidate, cadence)) return candidate;
    quarter += 1;
    if (quarter > 4) {
      quarter = 1;
      year += 1;
    }
  }

  // Fallback (should not reach here for valid cadences)
  return `${year}-Q${quarter}`;
}

/**
 * Return true when the given period falls within the specified cadence.
 */
export function periodMatchesCadence(
  period: string,
  cadence: AssessmentCadence = AssessmentCadence.SemiAnnual
): boolean {
  const isSemiAnnual = period.endsWith('-Q1') || period.endsWith('-Q3');
  const isAnnual = period.endsWith('-Q1');

  switch (cadence) {
    case AssessmentCadence.Quarterly:
      return true;
    case AssessmentCadence.Annual:
      return isAnnual;
    case AssessmentCadence.SemiAnnual:
    default:
      return isSemiAnnual;
  }
}

// ─── Display helpers ───────────────────────────────────────────────────────

export interface AssessmentScheduleInfo {
  /** ISO 8601 date string of the last Approved or Complete assessment, or null. */
  lastAssessedAt: string | null;
  /** Days since the last assessment, or null if never assessed. */
  daysSinceLastAssessment: number | null;
  /** The period string (e.g. "2026-Q1") for the next scheduled assessment. */
  nextDuePeriod: string;
  /** The first day of the next due period as an ISO 8601 date string. */
  nextDueAt: string;
  /** True when the next due date is in the past (assessment is overdue). */
  isOverdue: boolean;
}

/**
 * Derive schedule display info from the service's assessment history.
 *
 * @param assessments     All MaturityAssessment records for the service
 * @param cadence         Per-service cadence override (default: semi-annual)
 * @param now             Override "now" for testing
 */
export function computeAssessmentScheduleInfo(
  assessments: MaturityAssessment[],
  cadence: AssessmentCadence = AssessmentCadence.SemiAnnual,
  now: Date = new Date()
): AssessmentScheduleInfo {
  // Find the most recent Approved or Complete assessment
  const completed = assessments
    .filter((a) => a.status === 'Approved' || a.status === 'Complete')
    .sort((a, b) => b._createdAt.localeCompare(a._createdAt));

  const lastAssessment = completed[0] ?? null;

  const lastAssessedAt = lastAssessment
    ? (lastAssessment.assessmentDate ?? lastAssessment._createdAt)
    : null;

  const daysSinceLastAssessment =
    lastAssessedAt !== null
      ? Math.floor((now.getTime() - new Date(lastAssessedAt).getTime()) / (1000 * 60 * 60 * 24))
      : null;

  // Determine the next due period.
  //
  // "Next due" is the next period the scheduler would create a new assessment
  // for this service. It may be in the past (overdue) if an assessment was not
  // completed in the scheduled window.
  const currentPeriodStr = periodForDate(now);
  let nextDuePeriod: string;
  let isOverdue = false;

  if (lastAssessment === null) {
    // Never assessed — next due is the current period (if it matches cadence),
    // or the next matching period.
    if (periodMatchesCadence(currentPeriodStr, cadence)) {
      nextDuePeriod = currentPeriodStr;
    } else {
      nextDuePeriod = nextScheduledPeriod(currentPeriodStr, cadence);
    }
  } else {
    // The next due is one scheduled period after the last assessed period.
    const rawNext = nextScheduledPeriod(lastAssessment.assessmentPeriod, cadence);
    nextDuePeriod = rawNext;

    // If the raw next due period is already in the past, the service is overdue.
    // "In the past" means the period start date has already passed today.
    const rawNextStart = periodStart(rawNext);
    if (rawNextStart <= now) {
      // Check we are not still in that period (period started but hasn't ended)
      const rawNextPeriodStr = periodForDate(rawNextStart);
      if (rawNextPeriodStr < currentPeriodStr) {
        // The scheduled period has fully passed without an assessment → overdue
        isOverdue = true;
        // Show the current (or next matching) period as "next due" so it is actionable
        if (periodMatchesCadence(currentPeriodStr, cadence)) {
          nextDuePeriod = currentPeriodStr;
        } else {
          nextDuePeriod = nextScheduledPeriod(currentPeriodStr, cadence);
        }
      }
    }
  }

  const nextDueAt = periodStart(nextDuePeriod).toISOString();

  return {
    lastAssessedAt,
    daysSinceLastAssessment,
    nextDuePeriod,
    nextDueAt,
    isOverdue,
  };
}

/**
 * Return a human-readable cadence label.
 */
export function cadenceLabel(cadence: AssessmentCadence = AssessmentCadence.SemiAnnual): string {
  switch (cadence) {
    case AssessmentCadence.Quarterly:
      return 'Quarterly';
    case AssessmentCadence.Annual:
      return 'Annual';
    case AssessmentCadence.SemiAnnual:
    default:
      return 'Semi-annual';
  }
}
